export const AVATAR_TEMPLATE = 'full_body_front_v1';
export const AVATAR_PROFILES = Object.freeze({
  PORTRAIT: 'portrait',
  MAP_SELECTED: 'map-selected',
  MAP_NORMAL: 'map-normal',
  MAP_FAR: 'map-far',
});
const EMPTY_RANDOM_CHOICE = Object.freeze({ randomWeight: 1 });

function partsById(config) {
  return new Map((config?.parts || []).map(part => [part.id, part]));
}

export function getPartPalette(part, config) {
  if (!part?.paletteId) return null;
  return (config?.palettes || []).find(palette => (
    palette.id === part.paletteId && palette.state === 'published'
  )) || null;
}

export function getEnabledPaletteSwatches(part, config) {
  const palette = getPartPalette(part, config);
  return [...(palette?.swatches || [])]
    .filter(swatch => swatch.enabled !== false)
    .sort((left, right) => (
      (left.sortOrder ?? 0) - (right.sortOrder ?? 0)
      || String(left.label || '').localeCompare(String(right.label || ''))
      || String(left.id || '').localeCompare(String(right.id || ''))
    ));
}

function validSwatch(part, swatchId, config) {
  if (!swatchId) return null;
  return getEnabledPaletteSwatches(part, config).find(swatch => swatch.id === swatchId) || null;
}

function defaultSwatch(part, config) {
  const swatches = getEnabledPaletteSwatches(part, config);
  return swatches.find(swatch => swatch.id === part?.defaultSwatchId) || swatches[0] || null;
}

export function getSelectedSwatch(selection, part, config) {
  return validSwatch(part, selection?.swatches?.[part?.id], config) || defaultSwatch(part, config);
}

function normalizedSwatches(slots, candidateSwatches, config) {
  const parts = partsById(config);
  const candidates = Object.entries(candidateSwatches || {});
  const swatches = {};

  Object.entries(slots).forEach(([slotKey, partId]) => {
    const part = parts.get(partId);
    if (!part) return;

    let selected = validSwatch(part, candidateSwatches?.[partId], config);
    if (!selected && part.paletteId) {
      const transferable = candidates.find(([candidatePartId, candidateSwatchId]) => {
        const candidatePart = parts.get(candidatePartId);
        return candidatePart?.slotKey === slotKey
          && candidatePart.paletteId === part.paletteId
          && validSwatch(part, candidateSwatchId, config);
      });
      if (transferable) selected = validSwatch(part, transferable[1], config);
    }
    selected ||= defaultSwatch(part, config);
    if (selected) swatches[partId] = selected.id;
  });

  return swatches;
}

export function fallbackSelection(config) {
  const preset = config?.presets?.find(item => item.id === 'fallback_default' && item.state === 'published');
  const selection = preset?.selection || {};
  const slots = { ...(selection.slots || {}) };
  (config?.layerSlots || []).forEach(slot => {
    if (!slots[slot.key] && slot.defaultPartId) slots[slot.key] = slot.defaultPartId;
  });
  const swatches = normalizedSwatches(slots, selection.swatches, config);
  return {
    templateKey: config?.templateKey || AVATAR_TEMPLATE,
    configRevision: config?.configRevision || 1,
    slots,
    swatches,
    shirtOverTrousers: selection.shirtOverTrousers === true,
  };
}

export function normalizeSelection(selection, config) {
  const fallback = fallbackSelection(config);
  const slots = { ...fallback.slots, ...(selection?.slots || {}) };
  const swatches = normalizedSwatches(
    slots,
    { ...fallback.swatches, ...(selection?.swatches || {}) },
    config,
  );
  return {
    templateKey: config?.templateKey || AVATAR_TEMPLATE,
    configRevision: config?.configRevision || 1,
    slots,
    swatches,
    shirtOverTrousers: selection?.shirtOverTrousers == null
      ? fallback.shirtOverTrousers
      : selection.shirtOverTrousers === true,
  };
}

export function hiddenSlots(selection, config) {
  const parts = new Map((config?.parts || []).map(part => [part.id, part]));
  return new Set(Object.values(selection?.slots || {}).flatMap(partId => parts.get(partId)?.hidesSlots || []));
}

export function getRenderableParts(selection, config, renderProfile, includeDrafts = false) {
  const normalized = normalizeSelection(selection, config);
  const hidden = hiddenSlots(normalized, config);
  const slots = new Map((config?.layerSlots || []).map(slot => [slot.key, slot]));
  const parts = new Map((config?.parts || []).map(part => [part.id, part]));
  const allowedStates = includeDrafts ? new Set(['draft', 'published', 'retired']) : new Set(['published', 'retired']);

  const renderable = Object.entries(normalized.slots)
    .map(([slotKey, partId]) => ({ slot: slots.get(slotKey), part: parts.get(partId) }))
    .filter(({ slot, part }) => slot && part && allowedStates.has(part.state))
    .filter(({ slot, part }) => !hidden.has(slot.key) || part.hidesSlots?.includes(slot.key))
    .filter(({ slot, part }) => {
      if ((slot.key === 'background' || slot.key === 'portrait_fx') && renderProfile !== AVATAR_PROFILES.PORTRAIT) {
        return false;
      }
      const slotVisible = !slot.visibilityProfiles?.length || slot.visibilityProfiles.includes(renderProfile);
      const partVisible = !part.visibilityProfiles?.length || part.visibilityProfiles.includes(renderProfile);
      return slotVisible && partVisible;
    });
  return sortAvatarLayers(renderable, normalized);
}

export function sortAvatarLayers(layers, selection) {
  const ordered = [...layers].sort((left, right) => (
    left.slot.zIndex - right.slot.zIndex || left.slot.sortOrder - right.slot.sortOrder
  ));
  if (!selection?.shirtOverTrousers) return ordered;

  const shirtIndex = ordered.findIndex(({ slot }) => slot.key === 'clothes_base');
  const trousersIndex = ordered.findIndex(({ slot }) => slot.key === 'trousers');
  if (shirtIndex < 0 || trousersIndex < 0) return ordered;

  const [shirt] = ordered.splice(shirtIndex, 1);
  const nextTrousersIndex = ordered.findIndex(({ slot }) => slot.key === 'trousers');
  ordered.splice(nextTrousersIndex + 1, 0, shirt);
  return ordered;
}

export function selectPart(selection, part, config) {
  const next = normalizeSelection(selection, config);
  const previousPartId = next.slots?.[part.slotKey];
  const previousPart = partsById(config).get(previousPartId);
  const previousSwatch = getSelectedSwatch(next, previousPart, config);
  const slots = { ...next.slots, [part.slotKey]: part.id };
  const swatches = { ...next.swatches };
  if (previousPartId && previousPartId !== part.id) delete swatches[previousPartId];
  if (previousPart?.paletteId === part.paletteId && validSwatch(part, previousSwatch?.id, config)) {
    swatches[part.id] = previousSwatch.id;
  }
  return { ...next, slots, swatches: normalizedSwatches(slots, swatches, config) };
}

export function selectSwatch(selection, part, swatchId, config) {
  const next = normalizeSelection(selection, config);
  const swatch = validSwatch(part, swatchId, config);
  if (next.slots?.[part?.slotKey] !== part?.id || !swatch) return next;
  return { ...next, swatches: { ...next.swatches, [part.id]: swatch.id } };
}

export function clearPart(selection, slotKey, config) {
  const next = normalizeSelection(selection, config);
  const slot = (config?.layerSlots || []).find(item => item.key === slotKey);
  if (!slot || slot.required) return next;

  const selectedPartId = next.slots?.[slotKey];
  const slots = { ...next.slots };
  const swatches = { ...next.swatches };
  delete slots[slotKey];
  if (selectedPartId) delete swatches[selectedPartId];

  return { ...next, slots, swatches };
}

export function isCompatible(selection, config) {
  const parts = new Map((config?.parts || []).map(part => [part.id, part]));
  const selected = Object.values(selection?.slots || {}).map(id => parts.get(id)).filter(Boolean);
  const tags = new Set(selected.flatMap(part => part.tags || []));
  return selected.every(part =>
    (part.requiresTags || []).every(tag => tags.has(tag))
    && (part.excludesTags || []).every(tag => !tags.has(tag)));
}

function weightedChoice(parts, random) {
  const total = parts.reduce((sum, part) => sum + Math.max(0, Number(part.randomWeight) || 0), 0);
  if (total <= 0) return parts[0];
  let cursor = random() * total;
  for (const part of parts) {
    cursor -= Math.max(0, Number(part.randomWeight) || 0);
    if (cursor <= 0) return part;
  }
  return parts[parts.length - 1];
}

export function randomizeSelection(config, random = Math.random) {
  const base = fallbackSelection(config);
  const slots = config?.layerSlots || [];
  for (let attempt = 0; attempt < 20; attempt += 1) {
    let candidate = { ...base, slots: { ...base.slots }, swatches: { ...base.swatches } };
    slots.forEach(slot => {
      const choices = (config?.parts || []).filter(part =>
        part.slotKey === slot.key
        && part.state === 'published'
        && part.starterEligible
        && part.templateKey === (config?.templateKey || AVATAR_TEMPLATE));
      const choice = weightedChoice(slot.required ? choices : [...choices, EMPTY_RANDOM_CHOICE], random);
      if (choice === EMPTY_RANDOM_CHOICE) {
        candidate = clearPart(candidate, slot.key, config);
      } else if (choice) {
        candidate = selectPart(candidate, choice, config);
      }
    });
    if (isCompatible(candidate, config)) return candidate;
  }
  return base;
}
